export const normalizePhone = (value: string | undefined) => {
  if (!value) return ''

  const onlyNums = value.replace(/\D/g, '').slice(0, 15)

  if (onlyNums.length <= 2) {
    return `(${onlyNums}`
  }
  if (onlyNums.length <= 6) {
    return `(${onlyNums.slice(0, 2)}) ${onlyNums.slice(2)}`
  }
  if (onlyNums.length === 7) {
    return `(${onlyNums.slice(0, 2)}) ${onlyNums.slice(2, 6)}-${onlyNums.slice(6)}`
  }
  if (onlyNums.length <= 10) {
    return `(${onlyNums.slice(0, 2)}) ${onlyNums.slice(2, 6)}-${onlyNums.slice(6)}`
  }
  if (onlyNums.length === 11) {
    return `(${onlyNums.slice(0, 2)}) ${onlyNums.slice(2, 7)}-${onlyNums.slice(7)}`
  }

  return `(${onlyNums.slice(0, 2)}) ${onlyNums.slice(2, 7)}-${onlyNums.slice(7, 11)}`
}

export const normalizeNumbersOnly = (value: string | undefined) => {
  if (!value) return ''

  return value.replace(/[\D]/g, '')
}

export const normalizeCnpj = (value: string | undefined) => {
  if (!value) return ''

  return value
    .replace(/[\D]/g, '')
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1')
}

export function maskFloat(value: string | undefined): string {
  if (!value) return ''

  value = value.replace(/\D/g, '')

  if (value.length === 1) return `0.0${value}`
  if (value.length === 2) return `0.${value}`

  const integerPart = value.slice(0, -2)
  const decimalPart = value.slice(-2)
  return `${parseInt(integerPart, 10)}.${decimalPart}`
}

export const normalizeCpf = (value: string | undefined) => {
  if (!value) return ''

  return value
    .replace(/[\D]/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1')
}

export const normalizeAgencyNumber = (value: string | undefined) => {
  if (!value) return ''

  return value.replace(/[\D]/g, '').replace(/(\d+)(\d)$/, '$1-$2')
}

export const normalizeAccountNumber = (value: string | undefined) => {
  if (!value) return ''

  return value.replace(/[\D]/g, '').replace(/(\d+)(\d)$/, '$1-$2')
}

export function normalizePercentageValue(value: string): number {
  const numericValue = value.replace(/[^\d,.-]/g, '').replace(',', '.')
  if (numericValue.indexOf('.') !== numericValue.lastIndexOf('.')) {
    return NaN
  }
  return Number(parseFloat(numericValue))
}

export const normalizeBRL = (value: string | undefined) => {
  if (!value) return ''

  return value
    .replace(/[\D]/g, '')
    .replace(/(\d)(\d{2})$/, '$1,$2')
    .replace(/(?=(\d{3})+(\D))\B/g, '.')
    .replace(/^/, 'R$ ')
}

export const normalizeDecimalCoordinate = (value: string | undefined) => {
  if (typeof value !== 'string') return ''
  const normalized = value
    .replace(/[^0-9.-]/g, '')
    .replace(/(\..*?)\..*/g, '$1')
    .replace(/^(?!-)[^\d]+/, '')
    .replace(/(?!^)-/, '')
  return normalized
}

export const normalizeDegreesAndMinutes = (value: string | undefined) => {
  if (typeof value !== 'string') return ''
  const normalized = value.replace(/[^-?\d]/g, '')
  return normalized
}

export const normalizeSeconds = (value: string | undefined) => {
  if (typeof value !== 'string') return ''
  const normalized = value.replace(/[^-?\d.]/g, '')
  return normalized
}

export const normalizeCarNumber = (value: string | undefined) => {
  if (!value) return ''

  const cleanedValue = value.replace(/[^A-Za-z0-9]/g, '')

  // Se tiver 2 caracteres, não vai adicionar o hífen
  if (cleanedValue.length <= 2) {
    return cleanedValue.toUpperCase()
  }

  // Se tiver 3 e até 9 caracteres, vai adicionar o primeiro hífen
  if (cleanedValue.length <= 9) {
    return cleanedValue
      .replace(/^([A-Za-z]{2})(\d{0,7})/, (_, p1, p2) => {
        return `${p1}-${p2}`
      })
      .toUpperCase()
  }

  // Se tiver mais de 9 caracteres, vai adicionar o segundo hífen
  return cleanedValue
    .replace(/^([A-Za-z]{2})(\d{7})([A-Za-z0-9]{0,32})/, (_, p1, p2, p3) => {
      return `${p1}-${p2}-${p3}`
    })
    .toUpperCase()
}

export const normalizeCibNumber = (value: string | undefined) => {
  if (!value) return ''

  const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()

  const prefix = cleaned.slice(0, 7)
  const suffix = cleaned.slice(7, 8)

  if (!prefix) return ''

  return suffix ? `${prefix}-${suffix}` : prefix
}

export const normalizeHectare = (value: string | undefined) => {
  if (!value) return ''

  return value.replace(/\D/g, '')
}

export const normalizeCep = (value: string | undefined) => {
  if (!value) return ''
  const onlyNums = /[\D]/g

  return value
    .replace(onlyNums, '')
    .slice(0, 8)
    .replace(/^([\d]{5})(\d)/, '$1-$2')
}

export const normalizePercentage = (value: string | undefined): string => {
  if (!value) return ''

  const numericValue = value
    .replace(/[^\d.]/g, '')
    .replace(/^0+(?=\d)/, '')
    .replace(/^(\.)/, '0$1')
    .replace(/(\.\d{2})\d+$/, '$1')

  return numericValue ? `${numericValue}%` : ''
}

export const normalizeCREA = (value: string | undefined): string => {
  if (!value) return ''

  return value
    .replace(/\D/g, '')
    .replace(/^(\d{6,7})(\d)/, '$1/$2')
    .replace(/(.{9})\d+/, '$1')
}

export const normalizeOAB = (value: string | undefined): string => {
  if (!value) return ''

  return value
    .replace(/[^a-zA-Z0-9]/g, '')
    .replace(/^(\d{6})([a-zA-Z])/, '$1/$2')
    .replace(/(.{8})\w+/, '$1')
}
